import {MatDateFormats, NativeDateAdapter} from '@angular/material/core';

function isValidDateTime(day: number, month: number, year: number, hour: number, minute: number): boolean {
    return day >= 1 && day <= 31 &&
        month >= 1 && month <= 12 &&
        year >= 1000 && year <= 9999 &&
        hour >= 0 && hour <= 23 &&
        minute >= 0 && minute <= 59;
}

export function customDateParsing(value: string, supportedFormats?: DateTimeFormat[]): Date | null {
    if (!value) {
        return null;
    }
    value = value.trim();
    const hasSupportedFormats = supportedFormats?.length > 0;
    const ddmmyyyyhhmmMatch = value.match(/^(\d{12})$/);
    if ((hasSupportedFormats && supportedFormats.includes('DDMMYYYYHHMM') && ddmmyyyyhhmmMatch) ||
        (!hasSupportedFormats && ddmmyyyyhhmmMatch)) {
        const dateStr = ddmmyyyyhhmmMatch[1];
        const day = parseInt(dateStr.substring(0, 2), 10);
        const month = parseInt(dateStr.substring(2, 4), 10);
        const year = parseInt(dateStr.substring(4, 8), 10);
        const hour = parseInt(dateStr.substring(8, 10), 10);
        const minute = parseInt(dateStr.substring(10, 12), 10);
        if (isValidDateTime(day, month, year, hour, minute)) {
            return new Date(year, month - 1, day, hour, minute);
        }
    }

    const ddmmyyyyHHMMMatch = value.match(/^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})$/);
    if ((hasSupportedFormats && supportedFormats.includes('DD.MM.YYYY HH:MM') && ddmmyyyyHHMMMatch) ||
        (!hasSupportedFormats && ddmmyyyyHHMMMatch)) {
        const [, day, month, year, hour, minute] = ddmmyyyyHHMMMatch;
        if (isValidDateTime(+day, +month, +year, +hour, +minute)) {
            return new Date(+year, +month - 1, +day, +hour, +minute);
        }
    }

    const ddmmyyyyMatch = value.match(/^(\d{8})$/);
    if ((hasSupportedFormats && supportedFormats.includes('DDMMYYYY') && ddmmyyyyMatch) ||
        (!hasSupportedFormats && ddmmyyyyMatch)) {
        const dateStr = ddmmyyyyMatch[1];
        const day = parseInt(dateStr.substring(0, 2), 10);
        const month = parseInt(dateStr.substring(2, 4), 10);
        const year = parseInt(dateStr.substring(4, 8), 10);
        if (isValidDateTime(day, month, year, 0, 0)) {
            return new Date(year, month - 1, day, 0, 0);
        }
    }

    const ddmmMatch = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if ((hasSupportedFormats && supportedFormats.includes('DD.MM.YYYY') && ddmmMatch) || (!hasSupportedFormats && ddmmMatch)) {
        const [, day, month, year] = ddmmMatch;
        if (isValidDateTime(+day, +month, +year, 0, 0)) {
            return new Date(+year, +month - 1, +day, 0, 0);
        }
    }
    return null;
}

export class DateTimeAdapter extends NativeDateAdapter {
    private supportedFormats?: DateTimeFormat[];
    private displayFormat?: string;

    override parse(value: string): Date | null {
        const parsedDate = customDateParsing(value, this.supportedFormats);
        if (parsedDate) {
            return parsedDate;
        }
        return super.parse(value);
    }

    override format(date: Date, displayFormat: string): string {
        if (!date) {
            return '';
        }

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear().toString();
        const hour = date.getHours().toString().padStart(2, '0');
        const minute = date.getMinutes().toString().padStart(2, '0');

        const fmt = this.displayFormat || displayFormat;

        switch (fmt) {
            case 'DD.MM.YYYY HH:MM':
                return `${day}.${month}.${year} ${hour}:${minute}`;
            case 'DD.MM.YYYY':
                return `${day}.${month}.${year}`;
            default:
                return super.format(date, fmt);
        }
    }

    setFormats(supportedFormats?: DateTimeFormat[], displayFormat?: string): void {
        this.supportedFormats = supportedFormats;
        this.displayFormat = displayFormat;
    }
}

export type DateTimeFormat = 'DDMMYYYYHHMM' | 'DDMMYYYY' | 'DD.MM.YYYY HH:MM' | 'DD.MM.YYYY';

export const DATE_TIME_FORMATS: MatDateFormats = {
    parse: {
        dateInput: 'DDMMYYYYHHMM',
    },
    display: {
        dateInput: 'DD.MM.YYYY HH:MM',
        dateA11yLabel: 'DD.MM.YYYY HH:MM',
        monthLabel: 'MMM',
        monthYearLabel: 'MMM YYYY',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

export const DATE_FORMATS: MatDateFormats = {
    parse: {
        dateInput: 'DDMMYYYY',
    },
    display: {
        dateInput: 'DD.MM.YYYY',
        dateA11yLabel: 'DD.MM.YYYY',
        monthLabel: 'MMM',
        monthYearLabel: 'MMM YYYY',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};
